import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import Breadcrumb from "../../../../components/common/Breadcrumbs";
import { DataTable } from "../../../../components/common/datatable/DataTable";
import { DataTableColumn, FilterOptions } from "../../../../components/common/datatable/DataTableTypes";
import { createClickableLink, queryParamsToObject } from "../../../../components/common/datatable/DataTableUtils";
import { SearchDatePickerWrapper } from "../../../../components/common/datatable/filter/SearchDatePickerWrapper";
import { SearchSelectWithList } from "../../../../components/common/datatable/filter/SearchSelectWrapper";
import { hasPermission, jwtDecode } from "../../../../helpers/utils/AccessControlUtils";
import { formatValueAsDate } from "../../../../helpers/utils/DateUtils";
import { getEnumDisplayNameByConstant } from "../../../../helpers/utils/EnumUtils";
import { useGetAllCustomersListQuery, useGetParentAllCustomerQuery } from "../../../customer/customerApi";
import { useGetBranchesByCustomerIdsQuery } from "../../../customer/branchApi";
import { useGetAllStatesQuery } from "../../../data/states/statesApi";
import { useGetAllCountyByStateAbbrQuery } from "../../../data/counties/countiesApi";
import { useSearchFollowUpTypesQuery } from "../../../tenantAdmin/followUpTypes/followUpTypesApi";
import { useLazyExportDailyUpdateReportDataQuery, useSearchDailyUpdateReportApiQuery } from "../report";
import { toDailyUpdateReportRequest } from "../dailyUpdateReportFilters";
import ReportType from "./ReportType";

interface Filters {
    intakeDate: string;
    createdDateFrom: string;
    createdDateTo: string;
    customerIds: string[];
    parentCustomerId: string;
    branchIds: string[];
    state: string;
    county: string;
    type: string[];
    productStatuses: string[];
    orderStatus: string[];
    followUpTypes: string[];
    customerHold: boolean | null;
}

type Option = { value: string | boolean; label: string };
type Customer = { id: string; customerName: string; customerNumber: string };
type SelectField = {
    key: keyof Filters;
    label: string;
    options: Option[];
    multi?: boolean;
    componentKey?: string | number;
} & Pick<FilterOptions, "dependentColumns" | "onDependencyUpdated">;

const dateFields: { key: "intakeDate" | "createdDateFrom" | "createdDateTo"; label: string }[] = [
    { key: "intakeDate", label: "Orders Received Date" },
    { key: "createdDateFrom", label: "Created Date - From" },
    { key: "createdDateTo", label: "Created Date - To" },
];

const toOptions = (items: Record<string, string>[] | undefined, label: string, value = "id"): Option[] =>
    (items || []).map(item => ({ value: item[value], label: item[label] }));

const optionalColumnVisibility = { canColumnShow: true, showByDefault: false };

/** Returns a date-only value in the authenticated user's zone, independent of browser timezone. */
const todayInZone = (zone: string) => {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" })
        .formatToParts(new Date());
    const part = (name: string) => parts.find(value => value.type === name)?.value;
    return `${part("year")}-${part("month")}-${part("day")}`;
};

/** Uses UTC only for date arithmetic after today's date has been resolved in the user's zone. */
const defaultFilters = (today: string): Filters => {
    const yesterday = new Date(`${today}T00:00:00Z`);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    return {
        intakeDate: yesterday.toISOString().slice(0, 10),
        createdDateFrom: "",
        createdDateTo: "",
        customerIds: [],
        parentCustomerId: "",
        branchIds: [],
        state: "TX",
        county: "",
        type: [],
        productStatuses: [],
        followUpTypes: [],
        customerHold: null,
        orderStatus: ["NEW", "COMPLETED", "PENDING", "AWAITING_LOCATE", "ISSUE", "AWAITING_PARTNER", "AWAITING_FULFILLMENT"],
    };
};

const useDailyUpdateReportSearch = (query: Record<string, any>) =>
    useSearchDailyUpdateReportApiQuery(query, { skip: !hasPermission(["DLYUPDREPO-R"]) });

/** Displays the authorized report rows using the same filters and calculations as its CSV export. */
const DailyUpdateReport = () => {
    const canView = hasPermission(["DLYUPDREPO-V"]);
    const canExport = hasPermission(["DLYUPDREPO-R", "DLYUPDREPO-B", "REPOHIS-C"], true);
    const location = useLocation();
    const [today] = useState(() => todayInZone(jwtDecode()?.userZoneId || "UTC"));
    const [defaults] = useState(() => defaultFilters(today));
    const [initialQuery] = useState<Partial<Filters>>(() => queryParamsToObject(location.search));
    const fieldDefaults: Partial<Filters> = Object.keys(initialQuery).length ? {} : defaults;
    const [selectedState, setSelectedState] = useState(initialQuery.state ?? fieldDefaults.state ?? "");
    const [selectedCustomers, setSelectedCustomers] = useState(initialQuery.customerIds || []);
    const [branchFilterKey, setBranchFilterKey] = useState(0);
    const [reportType, setReportType] = useState("DAILY_UPDATE_REPORT");
    const [error, setError] = useState("");
    const { data: enums } = useGetEnumsQuery("", { skip: !canView });
    const { data: customers } = useGetAllCustomersListQuery("", { skip: !canView || !hasPermission(["CUST-R"]) });
    const { data: parents } = useGetParentAllCustomerQuery("", { skip: !canView || !hasPermission(["CUST-R"]) });
    const branchCustomers = selectedCustomers.length ? selectedCustomers : (customers || []).map((customer: Customer) => customer.id);
    const { data: branches } = useGetBranchesByCustomerIdsQuery(branchCustomers, {
        skip: !canView || !hasPermission(["BRAN-R"]) || !branchCustomers.length,
    });
    const { data: states } = useGetAllStatesQuery("", { skip: !canView || !hasPermission(["STAT-R"]) });
    const { data: counties } = useGetAllCountyByStateAbbrQuery(selectedState, { skip: !canView || !selectedState });
    const { data: followUps } = useSearchFollowUpTypesQuery({ pageSize: 1000 }, { skip: !canView || !hasPermission(["FOLLUP-R"]) });
    const [exportReport] = useLazyExportDailyUpdateReportDataQuery();

    useEffect(() => {
        document.title = "Reports";
    }, []);

    const selectFields: SelectField[] = [
        {
            key: "customerIds", label: "Customers", multi: true, dependentColumns: ["branchIds"],
            options: (customers || []).map((customer: Customer) => ({
                value: customer.id, label: `${customer.customerName} (${customer.customerNumber})`,
            })),
        },
        { key: "parentCustomerId", label: "Parent Customer", options: toOptions(parents, "customerName"), dependentColumns: ["branchIds"] },
        {
            key: "branchIds", label: "Branches", options: toOptions(branches, "name"), multi: true,
            componentKey: branchFilterKey,
            onDependencyUpdated: (value: string[] | string | undefined) => {
                if (Array.isArray(value)) {
                    setSelectedCustomers(value);
                }
                setBranchFilterKey(previous => previous + 1);
            },
        },
        { key: "state", label: "State", options: toOptions(states, "stateName", "stateAbbreviation"), dependentColumns: ["county"] },
        {
            key: "county", label: "County", options: toOptions(counties, "countyName"), componentKey: selectedState,
            onDependencyUpdated: (value: string | undefined) => setSelectedState(value || ""),
        },
        { key: "type", label: "Product Types", options: toOptions(enums?.ProductType, "value", "constant"), multi: true },
        { key: "productStatuses", label: "Product Statuses", options: toOptions(enums?.ProductStatus, "value", "constant"), multi: true },
        { key: "orderStatus", label: "Order Statuses", options: toOptions(enums?.OrderStatus, "value", "constant"), multi: true },
        {
            key: "followUpTypes", label: "Follow-up Types", multi: true,
            options: [{ value: "no-follow-up", label: "No Follow-up" }, ...toOptions(followUps?.data, "name")],
        },
        {
            key: "customerHold", label: "Customer Hold",
            options: [{ value: true, label: "On hold" }, { value: false, label: "Not on hold" }],
        },
    ];

    const columns: DataTableColumn[] = [
        ...dateFields.map(field => ({
            label: field.key, accessor: `filter_${field.key}`, canSort: false,
            visibility: { canColumnShow: false, hasShowSetting: false },
            filterOptions: [{
                overrideFilterLabel: field.label, overrideFilterAccessor: field.key,
                filterComponent: <SearchDatePickerWrapper accessor={field.key} defaultValue={fieldDefaults[field.key]} maxDate={today} />,
            }],
        })),
        ...selectFields.map(field => ({
            label: field.key, accessor: `filter_${field.key}`, canSort: false,
            visibility: { canColumnShow: false, hasShowSetting: false },
            filterOptions: [{
                overrideFilterLabel: field.label, overrideFilterAccessor: field.key,
                filterComponent: React.cloneElement(SearchSelectWithList(field.options, "label", "value",
                    `Select ${field.label}...`, Boolean(field.multi), field.key), {
                        key: field.componentKey, defaultValue: fieldDefaults[field.key],
                    }),
                dependentColumns: field.dependentColumns,
                onDependencyUpdated: field.onDependencyUpdated,
            }],
        })),
        {
            label: "Order Number", accessor: "customerOrderNumber",
            cell: ({value, row}: any) => createClickableLink(`/orders/${row.original.orderId}`, value, undefined, false, "", ["ORDR-R"]),
        },
        { label: "TGX ID", accessor: "tgxId", visibility: optionalColumnVisibility },
        { label: "Iteration", accessor: "iterations", overrideSortColumn: "reorderNumber" },
        { label: "Customer", accessor: "customerName", canSort: false },
        {
            label: "Order Status", accessor: "orderStatus", canSort: false,
            cell: ({value}: any) => getEnumDisplayNameByConstant(enums?.OrderStatus, value) || value,
        },
        { label: "Seller", accessor: "sellerName", canSort: false, visibility: optionalColumnVisibility },
        { label: "Buyer", accessor: "buyerName", canSort: false, visibility: optionalColumnVisibility },
        { label: "County", accessor: "countyName", canSort: false },
        { label: "Address", accessor: "addressLine1", canSort: false },
        { label: "Legal Description", accessor: "legalDescription", canSort: false, visibility: optionalColumnVisibility },
        { label: "Created Date", accessor: "creationDate", cell: ({value}: any) => formatValueAsDate(value) },
        { label: "Age (Business Days)", accessor: "aging", canSort: false },
        { label: "Customer Hold", accessor: "customerHold", canSort: false, cell: ({value}: any) => value ? "Yes" : "No" },
        ...[
            { label: "Age Under 3 Business Days", accessor: "young" },
            { label: "Age 3–5 Business Days", accessor: "middle" },
            { label: "Age Over 5 Business Days", accessor: "old" },
        ].map(column => ({ ...column, canSort: false, visibility: optionalColumnVisibility, cell: ({value}: any) => value ? "Yes" : "No" })),
        { label: "Expected Closing Date", accessor: "expectedClosingDate", canSort: false, cell: ({value}: any) => formatValueAsDate(value) },
        { label: "Follow-up Product", accessor: "followUpInfo", canSort: false, visibility: optionalColumnVisibility },
        { label: "Follow-up Category", accessor: "category", canSort: false },
        { label: "Follow-up Comments", accessor: "comments", canSort: false },
    ];

    const exportData = async (query: Record<string, any>) => {
        setError("");
        if (!canExport) return;
        const request = toDailyUpdateReportRequest(query);
        if (!request.intakeDate) {
            setError("Orders Received Date is required.");
            return;
        }
        if (request.createdDateFrom && request.createdDateTo && request.createdDateFrom > request.createdDateTo) {
            setError("Created Date From must not be after Created Date To.");
            return;
        }
        const result = await exportReport(request);
        if (result.error) {
            const failure = result.error as { data?: { message?: string } };
            setError(failure.data?.message || "Unable to export the report. Please try again.");
        }
    };

    const resetFilters = () => {
        setSelectedState(defaults.state);
        setSelectedCustomers([]);
        setBranchFilterKey(previous => previous + 1);
        setError("");
    };

    if (!canView) {
        return <p role="alert" className="p-4">You do not have permission to view this report.</p>;
    }

    return (
        <div className="user-container px-4 py-2">
            <div className="flex justify-between">
                <Breadcrumb crumbs={[{ content: "Home", url: "/home" }, { content: "Daily Update Report", url: "" }]} />
            </div>
            <ReportType type={reportType} setType={setReportType} />
            <DataTable
                columns={columns}
                actions={{ fetchAction: useDailyUpdateReportSearch, exportAction: exportData, exportDisabled: !canExport }}
                defaultFilter={defaults}
                defaultFilterFormExpanded={true}
                resetFilterToDefault={true}
                setResetDefaultFilter={resetFilters}
                defaultSortOptions={{ accessor: "creationDate", direction: "desc" }}
                userSettingGroup="Report Daily Update"
                basePermission="DLYUPDREPO"
            />
            {error && <p role="alert" className="pt-2">{error}</p>}
        </div>
    );
};

export default DailyUpdateReport;
